import { Box, Chip, Typography } from "@mui/material"
import { CloseFilledOutlined } from "@mui-symbols-material/w300"
import type { FC, MutableRefObject } from "react"

import { AssociateProductType } from "../../../types"

import type { CompetitorProduct, Product } from "@/graphql/codegen/graphql"
import { getProductId, getProductName } from "@/utils/equivalentProducts"

interface SelectedProductsChipsProps {
  productType: AssociateProductType
  newSelections: Record<string, boolean>
  cachedProducts: MutableRefObject<{
    [AssociateProductType.Akrochem]: Product[]
    [AssociateProductType.Competitor]: CompetitorProduct[]
  }>
  onRemove: (type: AssociateProductType, id: string) => void
}

/**
 * Displays selected products as removable chips
 * Works for both Akrochem and Competitor product types
 * Reads product data from cachedProducts ref
 */
export const SelectedProductsChips: FC<SelectedProductsChipsProps> = ({
  productType,
  newSelections,
  cachedProducts,
  onRemove,
}) => {
  const productsCache = cachedProducts.current[productType]

  const chipsData = Object.keys(newSelections)
    .filter((id) => newSelections[id])
    .map((id) => {
      const product = productsCache.find((p) => getProductId(p) === id)
      const label = product ? getProductName(product) : `Product ${id}`

      return { id, label }
    })

  const count = chipsData.length

  if (count === 0) return null

  const productLabel = productType === AssociateProductType.Akrochem ? "Akrochem" : "Competitor"

  return (
    <Box className='flex flex-col gap-2'>
      <Typography variant='body2' className='font-medium text-gray-700'>
        {count} {productLabel} Product{count > 1 ? "s" : ""} selected
      </Typography>
      <Box className='flex flex-wrap gap-2'>
        {chipsData.map(({ id, label }) => (
          <Chip
            key={id}
            label={label}
            variant='filled'
            size='small'
            onDelete={() => onRemove(productType, id)}
            deleteIcon={<CloseFilledOutlined className='!size-4' aria-label={`Remove ${label}`} />}
            className='!cursor-default rounded-sm bg-gray-200 focus:outline focus:outline-blue-200'
            sx={{
              "& .MuiChip-deleteIcon": {
                color: "rgba(0, 0, 0, 0.54)",
                "&:hover": {
                  color: "rgba(0, 0, 0, 0.87)",
                },
                "&:focus": {
                  outline: "2px solid rgb(59 130 246 / 0.5)",
                  outlineOffset: "-2px",
                },
              },
            }}
          />
        ))}
      </Box>
    </Box>
  )
}
